import type {Page} from '@PosterWhiteboard/page/page.class';
import type {GalleryTemplateDataObject, PageObject, TemplateDataObject} from '@PosterWhiteboard/page/page.types';
import {ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import type {AudioItemObject} from '@PosterWhiteboard/classes/audio-clips/audio-item.class';
import type {ItemTransitionObject} from '@PosterWhiteboard/page/transitions/page-transitions.types';
import {createItemFromObject} from '@PosterWhiteboard/items/item/item-factory';
import {getItemPageEndTime} from '@PosterWhiteboard/items/item/item-timing.library';
import {commitPosterMutation} from '@PosterWhiteboard/poster/commit-poster-mutation';
import {DEFAULT_NON_VIDEO_PAGE_DURATION} from '@PosterWhiteboard/libraries/video-page';
import {quantizeTimeSeconds} from '@Utils/math.util';
import {getUniqueString} from '@Utils/string.util';

export interface TimelineAppendPayload {
  /** Every imported item in stacking order, timings shifted onto the target timeline. */
  itemObjects: ItemObject[];
  audioItemObjects: AudioItemObject[];
  transitionObjects: ItemTransitionObject[];
  /** Where the last imported page ends on the target timeline. */
  endTime: number;
  /**
   * How many rows the target's EXISTING item-track bars move down so they stay against the
   * background when an imported page is taller than the track. 0 when the track is at least as tall.
   */
  existingSubRowShift: number;
}

/** What the appended source is, for the page's template credit: a gallery template carries its designer, a design does not. */
export interface TimelineAppendSourceCredit {
  templateData: TemplateDataObject;
  galleryTemplateData: GalleryTemplateDataObject | null;
}

/**
 * Imports whole pages (a design, a template, or one of their pages) onto the CURRENT page's timeline,
 * one after another, starting where the current background sequence ends. Each source page keeps its
 * own internal timings; a static source page gets the same default slot a Blank background gets.
 * The import lands as a single undo step, the page's template credit included.
 */
export const appendPageObjectsToPageTimeline = async (page: Page, pageObjects: PageObject[], sourceCredit: TimelineAppendSourceCredit): Promise<void> => {
  if (pageObjects.length === 0) {
    return;
  }
  if (!page.isVideo()) {
    // The current page had no timeline: give its existing design the default slot first, so the
    // imported pages tile in AFTER it instead of on top of it.
    page.items.refitAllItemsToConvertedPageSpan(DEFAULT_NON_VIDEO_PAGE_DURATION);
  }

  let firstImportedBackgroundUid: string | undefined;
  try {
    // A page with items but no background would start the imported sequence at its visual end while
    // the canvas shows no background before it; a default one over the existing design makes the
    // sequence contiguous from 0 — what the track draws and what plays agree.
    await page.items.addDefaultBackgroundIfNone();
    const payload = shiftPageObjectsForTimelineAppend(pageObjects, getTimelineAppendStartTime(page), page.items.getHighestTimelineSubRow());
    firstImportedBackgroundUid = payload.itemObjects.find((itemObject) => itemObject.isBackground)?.uid;
    await addImportedContentToPage(page, payload);
    creditAppendedSourceIfUncredited(page, sourceCredit);
  } finally {
    // The adds above stay silent, so this is the one history/redux commit — and it must run even
    // when a media load fails midway, or the history snapshot drifts from the live poster.
    page.items.refreshItemsVisibility();
    page.pageWatermark.refreshWatermark();
    commitPosterMutation(page);
  }
  if (firstImportedBackgroundUid) {
    await page.selectBackground(firstImportedBackgroundUid);
  }
};

/**
 * First template wins. A page that already credits a gallery template keeps it: the credit is what
 * pays that designer's royalty, and a later append must not take it. A page with no gallery credit
 * takes an appended TEMPLATE as its template outright; an appended DESIGN carries no designer, so it
 * only fills an empty templateData.
 */
const creditAppendedSourceIfUncredited = (page: Page, sourceCredit: TimelineAppendSourceCredit): void => {
  if (page.galleryTemplateData) {
    return;
  }
  if (sourceCredit.galleryTemplateData) {
    page.templateData = sourceCredit.templateData;
    page.galleryTemplateData = sourceCredit.galleryTemplateData;
    return;
  }
  page.templateData ??= sourceCredit.templateData;
};

const addImportedContentToPage = async (page: Page, payload: TimelineAppendPayload): Promise<void> => {
  // The existing design keeps its place next to the background; a taller import opens its extra
  // rows ABOVE it rather than pushing it up the track.
  page.items.shiftItemTrackSubRows({fromRow: 0, by: payload.existingSubRowShift});
  for (const itemObject of payload.itemObjects) {
    // eslint-disable-next-line no-await-in-loop -- items must land in stacking order
    const item = await createItemFromObject(page, itemObject);
    // eslint-disable-next-line no-await-in-loop -- items must land in stacking order
    await page.addItem(item, {undoable: false, fireEvent: false, updateRedux: false, skipSubRowRecalc: true});
  }
  page.items.recalculateTimelineSubRows();

  if (payload.audioItemObjects.length > 0) {
    await page.audioClips.updateFromObject({audioItemsHashMap: mergeAudioItemsWithPage(page, payload.audioItemObjects)}, {undoable: false, updateRedux: false, fireModifiedEvent: false});
  }
  if (payload.transitionObjects.length > 0) {
    page.transitions.updateFromObject({transitionsHashMap: mergeTransitionsWithPage(page, payload.transitionObjects)});
  }
};

/** The imported sequence starts right after the last background; a page with no backgrounds appends after its furthest item. */
const getTimelineAppendStartTime = (page: Page): number => {
  const lastBackground = page.items.getLastBackground();
  if (lastBackground) {
    return lastBackground.getPageEndTime();
  }
  return page.getVisualDuration();
};

/**
 * Pure: lays the source pages out back to back from `startTime`. A timed page keeps its internal
 * timings; a static page (visual duration 0) is given the default page slot with every item
 * spanning it, mirroring how a static page converts when its timeline begins.
 * `targetHighestSubRow` is the row the target track already keeps against the background; every source
 * page's rows are bottom-aligned to it so imported bars land next to the background rather than at the
 * top row. -1 means the target has no item-track rows yet. When a source page is taller than the target
 * track, the payload's `existingSubRowShift` says how far the target's own bars move down to stay
 * background-adjacent too.
 */
export const shiftPageObjectsForTimelineAppend = (pageObjects: PageObject[], startTime: number, targetHighestSubRow = -1): TimelineAppendPayload => {
  const itemObjects: ItemObject[] = [];
  const audioItemObjects: AudioItemObject[] = [];
  const transitionObjects: ItemTransitionObject[] = [];
  let cursor = startTime;
  // A source page taller than the target track pushes the whole track down, so the row that ends up
  // against the background is the deepest of the target and every source page. Aligning all pages to
  // that one floor keeps each of them background-adjacent instead of leaving the shorter ones hanging.
  const backgroundAdjacentSubRow = Math.max(targetHighestSubRow, ...pageObjects.map(getHighestItemTrackSubRow));

  for (const pageObject of pageObjects) {
    const visualDuration = getPageObjectVisualDuration(pageObject);
    const pageDuration = visualDuration > 0 ? visualDuration : DEFAULT_NON_VIDEO_PAGE_DURATION;
    const isStaticPage = visualDuration === 0;

    const subRowShift = Math.max(0, backgroundAdjacentSubRow - getHighestItemTrackSubRow(pageObject));

    for (const itemUid of pageObject.items.itemsOrder) {
      const itemObject = pageObject.items.itemsHashMap[itemUid];
      if (!itemObject) {
        continue;
      }
      itemObjects.push(shiftItemObject(itemObject, {offset: cursor, pageDuration, subRowShift, spanWholePage: isStaticPage || isSpanlessBackground(itemObject)}));
    }

    for (const audioItemObject of Object.values(pageObject.audioClips.audioItemsHashMap)) {
      audioItemObjects.push({...audioItemObject, uid: getUniqueString(), onPageStartTime: quantizeTimeSeconds(cursor + audioItemObject.onPageStartTime)});
    }

    const importedItemUids = new Set(Object.keys(pageObject.items.itemsHashMap));
    for (const transition of Object.values(pageObject.transitions.transitionsHashMap)) {
      if (importedItemUids.has(transition.fromItemUid) && importedItemUids.has(transition.toItemUid)) {
        transitionObjects.push({...transition});
      }
    }

    cursor = quantizeTimeSeconds(cursor + pageDuration);
  }

  return {itemObjects, audioItemObjects, transitionObjects, endTime: cursor, existingSubRowShift: Math.max(0, backgroundAdjacentSubRow - targetHighestSubRow)};
};

/** The source page's deepest item-track row; -1 when it contributes nothing to the item track. */
const getHighestItemTrackSubRow = (pageObject: PageObject): number => {
  let highestSubRow = -1;
  for (const itemObject of Object.values(pageObject.items.itemsHashMap)) {
    if (isItemObjectForTimelineItemTrack(itemObject)) {
      highestSubRow = Math.max(highestSubRow, itemObject.timelineSubRow);
    }
  }
  return highestSubRow;
};

/** Object-level twin of `Item.isItemForTimelineItemTrack`: backgrounds and transcripts have their own tracks. */
const isItemObjectForTimelineItemTrack = (itemObject: ItemObject): boolean => {
  return !itemObject.isBackground && itemObject.gitype !== ITEM_TYPE.TRANSCRIPT;
};

interface ShiftItemOpts {
  offset: number;
  pageDuration: number;
  subRowShift: number;
  spanWholePage: boolean;
}

const shiftItemObject = (itemObject: ItemObject, {offset, pageDuration, subRowShift, spanWholePage}: ShiftItemOpts): ItemObject => {
  const timelineSubRow = isItemObjectForTimelineItemTrack(itemObject) ? itemObject.timelineSubRow + subRowShift : itemObject.timelineSubRow;
  if (spanWholePage) {
    return {...itemObject, timelineSubRow, onPageStartTime: quantizeTimeSeconds(offset), onPageEndTime: quantizeTimeSeconds(offset + pageDuration)};
  }
  return {
    ...itemObject,
    timelineSubRow,
    onPageStartTime: quantizeTimeSeconds(offset + itemObject.onPageStartTime),
    onPageEndTime: quantizeTimeSeconds(offset + getItemPageEndTime(itemObject)),
  };
};

const isSpanlessBackground = (itemObject: ItemObject): boolean => {
  return itemObject.isBackground && getItemPageEndTime(itemObject) - itemObject.onPageStartTime <= 0;
};

/** Object-level twin of `Page.getVisualDuration`: the background span sum or the furthest item end, whichever is larger. */
export const getPageObjectVisualDuration = (pageObject: PageObject): number => {
  let backgroundsSum = 0;
  let maxItemEndTime = 0;
  for (const itemObject of Object.values(pageObject.items.itemsHashMap)) {
    if (itemObject.isBackground) {
      backgroundsSum += getItemPageEndTime(itemObject) - itemObject.onPageStartTime;
    } else {
      maxItemEndTime = Math.max(maxItemEndTime, getItemPageEndTime(itemObject));
    }
  }
  return Math.max(backgroundsSum, maxItemEndTime);
};

const mergeAudioItemsWithPage = (page: Page, audioItemObjects: AudioItemObject[]): Record<string, AudioItemObject> => {
  const merged: Record<string, AudioItemObject> = {};
  for (const [uid, audioItem] of Object.entries(page.audioClips.audioItemsHashMap)) {
    merged[uid] = audioItem.toObject();
  }
  for (const audioItemObject of audioItemObjects) {
    merged[audioItemObject.uid] = audioItemObject;
  }
  return merged;
};

const mergeTransitionsWithPage = (page: Page, transitionObjects: ItemTransitionObject[]): Record<string, ItemTransitionObject> => {
  const merged = page.transitions.toObject().transitionsHashMap;
  for (const transitionObject of transitionObjects) {
    merged[transitionObject.uid] = transitionObject;
  }
  return merged;
};
